/**
 * HubSlideOutPanel -- Slide-out AI Hub accessible from any page.
 *
 * Props:
 *   open    {boolean}   Whether the panel is visible
 *   onClose {function}  Called to close the panel
 *
 * Slides from the right, ~30% viewport width (min 360px, max 500px).
 * Overlay backdrop closes on click. Escape key closes.
 * Condensed layout -- no left panel (space constraint).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import FormattedContent from "./FormattedContent";

function fmtTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ct = new Date(d.toLocaleString("en-US", { timeZone: "America/Chicago" }));
  const hh = String(ct.getHours()).padStart(2, "0");
  const mi = String(ct.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
}

export default function HubSlideOutPanel({ open, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("tier_2_analysis");
  const [loading, setLoading] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const preVoiceTextRef = useRef("");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // ── Escape key closes ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // ── Load models on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    fetch("/api/hub/models")
      .then((r) => (r.ok ? r.json() : []))
      .then(setModels)
      .catch((e) => console.error("HubSlideOutPanel: fetch /api/hub/models failed:", e));
  }, [open]);

  // ── Load recent conversations (PATCH-16: Fix 3 — default to new chat) ──
  useEffect(() => {
    if (!open) return;
    fetch("/api/hub/conversations?limit=20")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : data.conversations || data.items || [];
        setConversations(list);
        // Don't auto-select — start fresh (chat created on first send)
      })
      .catch((e) => console.error("HubSlideOutPanel: fetch /api/hub/conversations failed:", e));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset state when panel closes (PATCH-16: Fix 3) ────────────────
  useEffect(() => {
    if (!open) {
      setActiveConvoId(null);
      setMessages([]);
      setInputText("");
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        setIsRecording(false);
      }
    }
  }, [open]);

  // ── Scroll to bottom on new messages ───────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Load conversation ──────────────────────────────────────────────
  const loadConversation = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/hub/conversations/${id}`);
      if (res.ok) {
        const data = await res.json();
        setActiveConvoId(data.id);
        setMessages(data.messages || []);
        setSelectedModel(data.model_tier || "tier_2_analysis");
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Create new chat ────────────────────────────────────────────────
  const createChat = async () => {
    try {
      const res = await fetch("/api/hub/conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "chat", model_tier: selectedModel }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveConvoId(data.id);
        setMessages(data.messages || []);
        setConversations((prev) => [data, ...prev]);
      }
    } catch {
      // silent
    }
  };

  // ── Send message ───────────────────────────────────────────────────
  const sendMessage = async () => {
    // Stop mic if recording (PATCH-16: Fix 1)
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }
    if (!inputText.trim() || sending) return;
    // Auto-create chat if none active (PATCH-16: Fix 3 — lazy chat creation)
    let convoId = activeConvoId;
    if (!convoId) {
      try {
        const res = await fetch("/api/hub/conversations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode: "chat", model_tier: selectedModel }),
        });
        if (res.ok) {
          const data = await res.json();
          convoId = data.id;
          setActiveConvoId(data.id);
          setConversations((prev) => [data, ...prev]);
        } else return;
      } catch { return; }
    }
    if (!convoId) return;
    const text = inputText.trim();
    setInputText("");
    setSending(true);

    const tempMsg = { id: "temp-" + Date.now(), role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await fetch(`/api/hub/conversations/${convoId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          const assistantMsg = {
            id: data.message_id || "ai-" + Date.now(),
            role: "assistant",
            content: data.reply,
            model: data.model,
            cost: data.cost,
            created_at: new Date().toISOString(),
          };
          return [...prev, assistantMsg];
        });
      } else {
        const err = await res.json().catch(() => ({}));
        setMessages((prev) => [
          ...prev,
          { id: "err-" + Date.now(), role: "assistant", content: `Error: ${err.detail || "Failed to get response"}`, created_at: new Date().toISOString() },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: "err-" + Date.now(), role: "assistant", content: "Error: Network failure.", created_at: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
    }
  };

  // ── Textarea handling ──────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleTextareaInput = (e) => {
    setInputText(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 150) + "px";
  };

  const toggleVoice = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    // Snapshot whatever text exists before recording starts
    preVoiceTextRef.current = inputText.trimEnd();

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      // Rebuild full transcript from ALL results each time (no accumulator)
      let finals = "";
      let interim = "";
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finals += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      const spoken = (finals + interim).trim();
      const base = preVoiceTextRef.current;
      setInputText(base ? base + " " + spoken : spoken);
    };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 bg-white shadow-2xl flex flex-col transition-transform duration-200"
        style={{ width: "clamp(360px, 30vw, 500px)" }}
      >
        {/* Header */}
        <div className="h-12 border-b border-gray-200 flex items-center px-4 gap-3 flex-shrink-0">
          <span className="text-sm font-semibold text-gray-800">
            {"\u{1F4AC}"} AI Hub
          </span>
          <Link
            to="/hub"
            onClick={onClose}
            className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
          >
            Open Full Page
          </Link>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Conversation selector + model */}
        <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
          <select
            value={activeConvoId || ""}
            onChange={(e) => {
              if (e.target.value === "__new__") {
                createChat();
              } else if (e.target.value) {
                loadConversation(e.target.value);
              }
            }}
            className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-1 focus:ring-blue-500 truncate"
          >
            <option value="" disabled>Select conversation...</option>
            <option value="__new__">+ New Chat</option>
            {conversations.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title || "Untitled"}
              </option>
            ))}
          </select>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-1 focus:ring-blue-500 w-28 flex-shrink-0"
          >
            {models.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
            {models.length === 0 && <option value="tier_2_analysis">Sonnet</option>}
          </select>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-xs text-gray-400">Loading...</div>
          ) : !activeConvoId ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-400">Select a conversation or start a new one.</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-400">No messages yet. Type below to start.</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isUser = msg.role === "user";
              return (
                <div key={msg.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 ${
                      isUser
                        ? "bg-gray-100 text-gray-900"
                        : "bg-white border border-gray-200 text-gray-800"
                    }`}
                  >
                    {isUser ? (
                      <p className="text-xs whitespace-pre-wrap font-sans leading-relaxed break-words">{msg.content}</p>
                    ) : (
                      <FormattedContent content={msg.content} maxHeight="none" />
                    )}
                    <div className={`text-[9px] mt-1 ${isUser ? "text-gray-400 text-right" : "text-gray-400"}`}>
                      {fmtTimestamp(msg.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex items-start">
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Thinking...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 p-3 flex-shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={handleTextareaInput}
              onKeyDown={handleKeyDown}
              placeholder={sending ? "Waiting..." : "Type a message..."}
              disabled={sending || !activeConvoId}
              rows={1}
              className="flex-1 text-xs border border-gray-300 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              style={{ maxHeight: "150px" }}
            />
            <button
              onClick={toggleVoice}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                isRecording
                  ? "bg-red-100 text-red-600 hover:bg-red-200 animate-pulse"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              title={isRecording ? "Stop recording" : "Voice input"}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0m7 7v4m-4 0h8m-4-12a3 3 0 00-3 3v4a3 3 0 006 0V8a3 3 0 00-3-3z" />
              </svg>
            </button>
            <button
              onClick={sendMessage}
              disabled={sending || !inputText.trim() || !activeConvoId}
              className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
